import { forwardRef, MutableRefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { PlayerSetup, Song } from '~/interfaces';
import { VideoPlayerRef, VideoState } from '~/modules/elements/video-player/index';
import { getScoreGutter } from '~/modules/game-engine/drawing/calculate-data';
import CanvasDrawing from '~/modules/game-engine/drawing/index';
import fragShader from '~/modules/game-engine/drawing/shaders/shader.frag?raw';
import vertShader from '~/modules/game-engine/drawing/shaders/shader.vert?raw';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import PlayerScores from '~/routes/game/singing/game-overlay/components/player-scores';
import SkipIntro from '~/routes/game/singing/game-overlay/components/skip-intro';
import SkipOutro from '~/routes/game/singing/game-overlay/components/skip-outro';
import { GraphicSetting, MobilePhoneModeSetting, useSettingValue } from '~/routes/settings/settings-state';

import GameState from '../../../../modules/game-engine/game-state/game-state';
import DurationBar from './components/duration-bar';
import Lyrics from './components/lyrics';

interface Props {
  song: Song;
  currentStatus: VideoState;
  width: number;
  height: number;
  onSongEnd?: () => void;
  playerSetups: PlayerSetup[];
  duration: number;
  effectsEnabled: boolean;
  videoPlayerRef: MutableRefObject<VideoPlayerRef | null>;
  isPauseMenuVisible: boolean;
  /** Online mode: only the host gets the skip-intro prompt. */
  skipIntroEnabled?: boolean;
  /** Online mode: routes the skip through the room server so it applies to everyone. */
  onSkipIntro?: (targetTimeSec: number) => void;
  /** Online mode: the player number leading the room, or null while nobody leads it (a tie, or no
   * scores yet). The local game leaves this undefined and derives the medal from the players on this
   * device — in a room that is only ever this singer, so everyone would be crowned first. */
  leadingPlayerNumber?: PlayerNumber | null;
  onOpenPauseMenu?: () => void;
}

const MAX_RENDER_RESOLUTION_W = 1920;

const GameOverlay = forwardRef(function (
  {
    currentStatus,
    width,
    height,
    playerSetups,
    onSongEnd,
    effectsEnabled,
    videoPlayerRef,
    isPauseMenuVisible,
    onOpenPauseMenu,
    duration,
    skipIntroEnabled = true,
    onSkipIntro,
    leadingPlayerNumber,
  }: Props,
  fRef,
) {
  'use no memo'; // React Compiler: reads GameState/PlayersManager mutable singletons directly during render (getPlayerScore, getSingSetup, getPlayers), so the compiler can cache stale JSX (e.g. the score display never updating).
  const [graphicLevel] = useSettingValue(GraphicSetting);
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawer = useRef<CanvasDrawing | null>(null);
  const lyrics = useRef<HTMLDivElement | null>(null);

  const overlayWidth = MAX_RENDER_RESOLUTION_W;
  const overlayHeight = overlayWidth * (height / width);

  const overlayScaleFactor = overlayHeight / height;

  useImperativeHandle(fRef, () => {
    return {
      pause: () => drawer.current?.pause(),
      resume: () => drawer.current?.resume(),
    };
  }, []);

  useEffect(() => {
    GameState.startInputMonitoring();

    return () => {
      GameState.stopInputMonitoring();
    };
  }, []);

  // const resolutionScaleFactor = overlayWidth / MAX_RENDER_RESOLUTION_W;

  // Kept in state as well as handed to the drawer, so the DOM lane decorations can place
  // themselves against exactly the same geometry the canvas draws with.
  const [paddingVertical, setPaddingVertical] = useState(0);

  useEffect(() => {
    if (!canvas.current || !lyrics.current) return;

    const padding = lyrics.current.offsetHeight * overlayScaleFactor;
    setPaddingVertical(padding);

    drawer.current = new CanvasDrawing(
      canvas.current,
      padding,
      // resolutionScaleFactor,
    );
    drawer.current.start();

    return () => {
      drawer.current?.end();
    };
  }, [overlayScaleFactor]);

  useEffect(() => {
    if (isPauseMenuVisible && drawer.current?.isPlaying()) {
      drawer.current?.pause();
    } else if (!isPauseMenuVisible && !drawer.current?.isPlaying()) {
      drawer.current?.resume();
    }
  }, [isPauseMenuVisible]);

  useEffect(() => {
    if (currentStatus === VideoState.ENDED && onSongEnd) {
      onSongEnd();
    }
  }, [currentStatus, onSongEnd]);

  const players = PlayersManager.getPlayers();
  // Singers sharing one lane share its lyrics too, so only a duet's two parts get a band each.
  const sharedLane = GameState.isMergedTrack();
  const showMultipleLines = !mobilePhoneMode && !sharedLane && players.length > 1;

  return (
    <div className="relative flex h-full flex-col font-bold text-white">
      {graphicLevel === 'high' && (
        <>
          <script type={'x-shader/x-fragment'} id={'plane-fs'}>
            {fragShader}
          </script>
          <script id="plane-vs" type="x-shader/x-vertex">
            {vertShader}
          </script>
          <div id="canvas" className="absolute inset-0 z-[10000] h-full w-full" />
        </>
      )}
      <div id="plane" className="absolute inset-0 h-full w-full" style={!effectsEnabled ? { opacity: 0 } : undefined}>
        <canvas
          ref={canvas}
          width={overlayWidth}
          height={overlayHeight}
          data-sampler="planeTexture"
          className="h-full w-full"
        />
      </div>
      {effectsEnabled && (
        <>
          {skipIntroEnabled && (
            <SkipIntro
              playerRef={videoPlayerRef}
              isEnabled={!isPauseMenuVisible}
              onSkip={onSkipIntro}
              onOpenPauseMenu={onOpenPauseMenu}
            />
          )}
          <SkipOutro onSongEnd={onSongEnd} isEnabled={!isPauseMenuVisible} onOpenPauseMenu={onOpenPauseMenu} />
        </>
      )}
      {effectsEnabled && paddingVertical > 0 && (
        <PlayerScores
          width={overlayWidth}
          height={overlayHeight}
          paddingVertical={paddingVertical}
          leadingPlayerNumber={leadingPlayerNumber}
        />
      )}
      <DurationBar
        players={playerSetups}
        currentStatus={currentStatus}
        duration={duration}
        rightInset={
          effectsEnabled
            ? `${(getScoreGutter({ width: overlayWidth, height: overlayHeight }) / overlayWidth) * 100}%`
            : undefined
        }
      />
      <div className="py-5">
        {showMultipleLines && (
          <Lyrics
            player={players[0]}
            effectsEnabled={effectsEnabled}
            showStatusForAllPlayers={players.length > 2}
            announcesBreak
          />
        )}
      </div>
      {/* Holds the bands apart; the scores are placed against the lanes rather than flowed here. */}
      <div className="flex-1" />
      <div className="py-5" ref={lyrics}>
        <Lyrics
          showStatusForAllPlayers={players.length > 2}
          player={sharedLane ? players[0] : players.at(-1)!}
          bottom
          effectsEnabled={effectsEnabled}
          /* The upper band announces the break; with one singer it is the only band there is. */
          announcesBreak={!showMultipleLines}
        />
      </div>
    </div>
  );
});

export default GameOverlay;
